import { useState } from "react";
import { Outlet, useSearchParams } from "react-router";
import {
  Checkbox,
  Flex,
  Divider,
  Button,
  SkeletonLoader,
} from "@bdo/cornerstone-ui";
import { ErrorBoundary, FeedbackPanel, Footer, Sidebar } from "@/components";
import { ManageAccessPanel } from "@/features/workflow";
import { useAuth, useFeatureFlags, useResizableSidebar } from "@/hooks";
import { useGetWorkflowDefinitionsQuery } from "@/app/workflows/workflowDefinitionsApi";
import {
  FilterModal,
  type WorkflowDefinitionFilter,
} from "@/features/workflow-list/FilterModal";

function isSameWorkflowDefinitionFilter(
  first: WorkflowDefinitionFilter,
  second: WorkflowDefinitionFilter,
) {
  return (
    first.workflowDefinitionName === second.workflowDefinitionName &&
    first.version === second.version
  );
}

function getWorkflowDefinitionFilter(searchParams: URLSearchParams) {
  const names = searchParams.getAll("workflowDefinitionName");
  const versions = searchParams.getAll("workflowDefinitionVersion");
  const pairedCount = Math.min(names.length, versions.length);

  return Array.from({ length: pairedCount }, (_, index) => ({
    workflowDefinitionName: names[index],
    version: versions[index],
  }));
}

export function Root() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { isAuthenticated } = useAuth();
  const { isArchiveEnabled } = useFeatureFlags();
  const { sidebarWidth, handleMouseDown, handleDoubleClick } =
    useResizableSidebar();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusFilter = searchParams.getAll("status");
  const workflowDefinitionFilter = getWorkflowDefinitionFilter(searchParams);

  function handleUpdateStatus(value: string, isSelected: boolean) {
    setSearchParams((searchParams) => {
      if (isSelected) {
        searchParams.append("status", value);
      } else {
        searchParams.delete("status", value);
      }
      searchParams.delete("page");

      return searchParams;
    });
  }

  function handleUpdateDefinitions(
    value: WorkflowDefinitionFilter,
    isSelected: boolean,
  ) {
    setSearchParams((searchParams) => {
      let nextFilters = getWorkflowDefinitionFilter(searchParams);

      if (isSelected) {
        if (
          !nextFilters.some((workflowDefinitionFilter) =>
            isSameWorkflowDefinitionFilter(workflowDefinitionFilter, value),
          )
        ) {
          nextFilters.push(value);
        }
      } else {
        nextFilters = nextFilters.filter(
          (workflowDefinitionFilter) =>
            !isSameWorkflowDefinitionFilter(workflowDefinitionFilter, value),
        );
      }

      searchParams.delete("workflowDefinitionName");
      searchParams.delete("workflowDefinitionVersion");

      for (const workflowDefinition of nextFilters) {
        searchParams.append(
          "workflowDefinitionName",
          workflowDefinition.workflowDefinitionName,
        );
        searchParams.append(
          "workflowDefinitionVersion",
          workflowDefinition.version,
        );
      }

      searchParams.delete("page");

      return searchParams;
    });
  }

  function handleApplyFilters(
    newStatusFilter: string[],
    newWorkflowDefinitionFilter: WorkflowDefinitionFilter[],
  ) {
    setSearchParams(() => {
      const searchParams = new URLSearchParams();
      for (const filter of newStatusFilter) {
        searchParams.append("status", filter);
      }
      for (const filter of newWorkflowDefinitionFilter) {
        searchParams.append(
          "workflowDefinitionName",
          filter.workflowDefinitionName,
        );
        searchParams.append("workflowDefinitionVersion", filter.version);
      }

      return searchParams;
    });
  }

  const { data: workflowDefinitions, isLoading: isLoadingDefinitions } =
    useGetWorkflowDefinitionsQuery({
      select: ["workflowDefinitionName", "version"],
    });

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <div
        className="h-screen overflow-hidden grid grid-rows-[minmax(0,1fr)_auto]"
        style={{ gridTemplateColumns: `${sidebarWidth}px minmax(0, 1fr)` }}
      >
        <Sidebar
          width={sidebarWidth}
          onResizeStart={handleMouseDown}
          onResizeDoubleClick={handleDoubleClick}
        >
          <Flex direction="column" gap="md">
            <span className="text-mono">Filter workflows</span>

            <span className="font-bold">Status</span>

            <Flex direction="column" gap="sm">
              <Checkbox
                value="RequiresAttention"
                label="Requires attention"
                isSelected={statusFilter.includes("RequiresAttention")}
                onChange={(isSelected) =>
                  handleUpdateStatus("RequiresAttention", isSelected)
                }
                data-testid="status-filter-requires-attention"
              />
              <Checkbox
                value="InProgress"
                label="In progress"
                isSelected={statusFilter.includes("InProgress")}
                onChange={(isSelected) =>
                  handleUpdateStatus("InProgress", isSelected)
                }
                data-testid="status-filter-in-progress"
              />
              <Checkbox
                value="Completed"
                label="Completed"
                isSelected={statusFilter.includes("Completed")}
                onChange={(isSelected) =>
                  handleUpdateStatus("Completed", isSelected)
                }
                data-testid="status-filter-completed"
              />
              {isArchiveEnabled && (
                <Checkbox
                  value="Archived"
                  label="Archived"
                  isSelected={statusFilter.includes("Archived")}
                  onChange={(isSelected) =>
                    handleUpdateStatus("Archived", isSelected)
                  }
                  data-testid="status-filter-archived"
                />
              )}
            </Flex>
            <Divider className="my-sm" />

            <span className="font-bold">Workflows</span>
            <Flex direction="column" gap="sm">
              {isLoadingDefinitions && (
                <div className="flex flex-col gap-sm">
                  <SkeletonLoader height="21px" width="100%" />
                  <SkeletonLoader height="21px" width="100%" />
                  <SkeletonLoader height="21px" width="100%" />
                  <SkeletonLoader height="21px" width="100%" />
                </div>
              )}
              {/* Only show first 5 */}
              {workflowDefinitions?.slice(0, 5)?.map((wd) => (
                <Checkbox
                  key={`${wd.workflowDefinitionName}-${wd.version}`}
                  value={`${wd.workflowDefinitionName}-${wd.version}`}
                  label={
                    <Flex direction="row" gap="2xs" className="overflow-hidden">
                      <span
                        className="overflow-hidden text-ellipsis whitespace-nowrap"
                        title={wd.workflowDefinitionName}
                      >
                        {wd.workflowDefinitionName}
                      </span>
                      <span className="whitespace-nowrap text-mono-dark">
                        (v{wd.version})
                      </span>
                    </Flex>
                  }
                  isSelected={workflowDefinitionFilter.some(
                    (selectedWorkflowDefinition) =>
                      selectedWorkflowDefinition.workflowDefinitionName ===
                        wd.workflowDefinitionName &&
                      selectedWorkflowDefinition.version === wd.version,
                  )}
                  onChange={(isSelected) =>
                    handleUpdateDefinitions(
                      {
                        workflowDefinitionName: wd.workflowDefinitionName,
                        version: wd.version,
                      },
                      isSelected,
                    )
                  }
                  data-testid={`workflow-filter-${wd.workflowDefinitionName}-${wd.version}`}
                  className="w-full flex flex-nowrap gap-sm"
                />
              ))}

              <Button variant="link" onClick={() => setIsModalOpen(true)}>
                View all
              </Button>

              <FilterModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                initialStatusFilter={statusFilter}
                initialWorkflowDefinitionFilter={workflowDefinitionFilter}
                workflowDefinitions={workflowDefinitions ?? []}
                onApplyFilters={handleApplyFilters}
              />
            </Flex>

            <Divider className="my-sm" />
          </Flex>
        </Sidebar>
        <ErrorBoundary>
          <div className="h-full min-h-0 min-w-0 overflow-hidden">
            <Outlet />
          </div>
        </ErrorBoundary>
        <div className="col-span-2">
          <Footer />
        </div>
      </div>
      <FeedbackPanel />
      <ManageAccessPanel />
    </>
  );
}
